import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { AbsencePanel } from './AbsencePanel';
import { NotificationPanel } from './NotificationPanel';
import { PasswordPanel } from './PasswordPanel';
import { useActivateMfa, useBeginMfaSetup, useDisableMfa, useSession } from './queries';

/**
 * Der zweite Faktor -- F5-16, K-145.
 *
 * **Drei Schritte, nicht zwei.** Kennwort bestaetigen, Geheimnis uebernehmen,
 * mit einem Code bestaetigen. Der letzte Schritt ist der wichtigste: Er
 * beweist, dass die Authenticator-App dasselbe Geheimnis hat und dass die
 * Uhren zusammenpassen. Ohne ihn verlangte die naechste Anmeldung einen Code,
 * den niemand erzeugen kann.
 *
 * **Kein QR-Bild.** Es braechte eine weitere Abhaengigkeit fuer etwas, das
 * jede Authenticator-Anwendung auch ueber die Eingabe des Geheimnisses
 * beherrscht -- und auf dem Telefon oeffnet der Verweis die Anwendung
 * unmittelbar.
 *
 * Das Geheimnis steht nur in diesem einen Mutationszustand und wird nicht
 * zwischengespeichert. Wer die Seite verlaesst, beginnt von vorn -- das ist
 * kein Mangel, sondern der Grund, warum es nirgends liegen bleibt.
 */
export function SecurityPage() {
  const { t } = useTranslation();
  const session = useSession();
  const begin = useBeginMfaSetup();
  const activate = useActivateMfa();
  const disable = useDisableMfa();

  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const enabled = session.data?.mfa_enabled ?? false;
  const required = session.data?.mfa_required ?? false;

  return (
    <>
      <PageHeader title={t('security.title')} subtitle={t('security.subtitle')} />

      {/* Das Kennwort zuerst: Wer diese Seite oeffnet, kommt haeufiger
          deswegen als wegen des zweiten Faktors -- den richtet man einmal
          ein. */}
      <PasswordPanel />

      <section className="card">
        <h2>
          {t('security.twoFactor')}{' '}
          {enabled ? (
            <Badge tone="success">{t('security.active')}</Badge>
          ) : (
            <Badge tone={required ? 'danger' : 'neutral'}>{t('security.inactive')}</Badge>
          )}
        </h2>

        {required && !enabled ? (
          <div className="notice notice--warning" role="alert">
            <p>{t('security.requiredNotice')}</p>
          </div>
        ) : null}

        <p className="table__muted">{t('security.explanation')}</p>
      </section>

      {!enabled ? (
        <>
          {begin.data === undefined ? (
            <section className="card">
              <h2>{t('security.step1')}</h2>
              <p className="table__muted">{t('security.step1Hint')}</p>

              <TextField
                label={t('auth.password')}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />

              {begin.isError ? <ErrorNotice error={begin.error} /> : null}

              <Button
                variant="primary"
                disabled={password.length === 0}
                busy={begin.isPending}
                onClick={() => {
                  begin.mutate(password, {
                    onSuccess: () => {
                      setPassword('');
                    },
                  });
                }}
              >
                {t('security.beginSetup')}
              </Button>
            </section>
          ) : (
            <section className="card">
              <h2>{t('security.step2')}</h2>
              <p className="table__muted">{t('security.step2Hint')}</p>

              <div className="field">
                <span className="field__label">{t('security.secret')}</span>
                <p>
                  <code>{begin.data.secret}</code>
                </p>
                <span className="field__hint">{t('security.secretHint')}</span>
              </div>

              <p>
                <a href={begin.data.provisioning_uri}>{t('security.openInApp')}</a>
              </p>

              <TextField
                label={t('auth.totpCode')}
                inputMode="numeric"
                autoComplete="one-time-code"
                hint={t('security.step3Hint')}
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                }}
              />

              {activate.isError ? <ErrorNotice error={activate.error} /> : null}

              <Button
                variant="primary"
                disabled={code.trim().length < 6}
                busy={activate.isPending}
                onClick={() => {
                  activate.mutate(code, {
                    onSuccess: () => {
                      setCode('');
                      begin.reset();
                    },
                  });
                }}
              >
                {t('security.activate')}
              </Button>
            </section>
          )}
        </>
      ) : null}

      <NotificationPanel />

      <AbsencePanel />

      {/* Abschalten gibt es nur ohne Pflicht. Der Server weist es sonst ab --
          hier wird die Maske gar nicht erst gezeigt, damit niemand einen
          Schalter sucht, den es fuer ihn nicht gibt. */}
      {enabled && !required ? (
        <section className="card">
          <h2>{t('security.disable')}</h2>
          <p className="table__muted">{t('security.disableHint')}</p>

          <TextField
            label={t('auth.password')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          <TextField
            label={t('auth.totpCode')}
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
            }}
          />

          {disable.isError ? <ErrorNotice error={disable.error} /> : null}

          <Button
            variant="secondary"
            disabled={password.length === 0 || code.trim().length < 6}
            busy={disable.isPending}
            onClick={() => {
              disable.mutate(
                { password, code },
                {
                  onSuccess: () => {
                    setPassword('');
                    setCode('');
                  },
                },
              );
            }}
          >
            {t('security.disableAction')}
          </Button>
        </section>
      ) : null}
    </>
  );
}
